import React, { useCallback, useEffect } from 'react';
import cx from 'classnames';
import Modal from '../../shared/modal';
import cssInJS from '../../shared/css';
import { useApp } from '../../hooks';
import { CloseIcon } from '../../icons';
import { createExtensionParams, createExtensionURL } from '../../models/extension';

interface Props {
  formId: string;
  visible: boolean;
  id: string;
  url?: string;
  width?: number;
  height?: number;
  title?: string;
  data?: any;
  onHide(source: string | null): void;
}

const DEFAULT_WIDTH = '100%';
const DEFAULT_HEIGHT = '100%';

interface ExtensionFrameParams {
  error?: any;
  frameURL: URL | null;
}

const Extension: React.FC<Props> = ({
  formId, visible, id, url, width, height, title, onHide, data,
}: Props) => {
  const app = useApp();

  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  const [frame, setFrame] = React.useState<ExtensionFrameParams>({
    error: null,
    frameURL: null,
  });

  const fetchFrame = useCallback(async () => {
    if (!url) {
      return;
    }

    try {
      const frameURL = await createExtensionURL(
        app.globals.config.attachments_url,
        app.api.token,
        formId,
        url,
      );

      setFrame({ frameURL, error: null });
    } catch (error) {
      setFrame({ frameURL: null, error });
    }
  }, [id, url, data]);

  const handleLoad = () => {
    if (iframeRef.current && url && frame.frameURL) {
      const message = JSON.stringify({
        command: 'initialize',
        params: createExtensionParams(url, id, data),
      });

      iframeRef.current.contentWindow?.postMessage(message, frame.frameURL.origin);
    }
  };

  useEffect(() => {
    fetchFrame();
  }, [fetchFrame]);

  const handleModalHide = () => {
    onHide(url ?? null);
  };

  const handleModalHidden = () => {
    setFrame({ frameURL: null, error: null });
  };

  const marginLeft = width != null ? -width / 2 : undefined;
  const marginTop = height != null ? -height / 2 : undefined;

  const left = width != null ? '50%' : undefined;
  const top = height != null ? '50%' : undefined;

  const style = {
    left,
    top,
    width: width ?? DEFAULT_WIDTH,
    height: height ?? DEFAULT_HEIGHT,
    marginLeft,
    marginTop,
  };

  return (
    <Modal
      show={visible}
      onHide={handleModalHide}
      onHidden={handleModalHidden}
    >
      { /* @ts-ignore */}
      <div className={cx('modal-content', styles.container)} style={style}>
        <button
          type="button"
          // @ts-ignore
          className={styles.exit}
          onClick={handleModalHide}
          onKeyDown={handleModalHide}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
        { /* @ts-ignore */ }
        {frame.error && (<p className={styles.error}>{frame.error.message}</p>)}
        {frame.frameURL && (
          <iframe
            key={id}
            sandbox="allow-scripts allow-same-origin"
            title={title}
            src={frame.frameURL.href}
            onLoad={handleLoad}
            // @ts-ignore
            className={styles.iframe}
            ref={iframeRef}
          />
        )}
      </div>
    </Modal>
  );
};

const styles = cssInJS({
  container: {
    position: 'absolute',
    zIndex: 1,
    minHeight: 300,
    backgroundColor: 'white',
  },

  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },

  exit: {
    position: 'absolute',
    right: 0,
    top: 0,
    width: 30,
    height: 30,
    paddingTop: 4,
    paddingRight: 6,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
  },

  error: {
    textAlign: 'center',
    paddingTop: 10,
  },
});

export default Extension;
